import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { AlertTriangle, Loader2 } from "lucide-react"
import { useNavigate, useParams, useSearchParams } from "react-router"
import { useConformityDetail } from "../../../api/conformityApi"
import { ConformityEditorModuleV2 } from "./ConformityEditorModuleV2"

const MC_BASE = "/material-compliance"

/**
 * Customer Studio → Conformity → Editor.
 *
 * Loads the declaration and hands it to the v2 editor. Every declaration is v2
 * — one document carrying each subscribed framework as its own block — so this
 * no longer branches on `detail.isV2`; the single-framework v1 editor it used
 * to choose between was removed with the v1 generate path.
 */
export function ConformityEditorModule() {
  const navigate = useNavigate()
  const { declarationId } = useParams<{ declarationId: string }>()
  const [params] = useSearchParams()
  const { translate } = useTranslation()

  const {
    data: detail,
    isLoading,
    isFetching: isDetailFetching,
    isError,
  } = useConformityDetail(declarationId ?? null)

  function goBackToList() {
    // The inbox at `/conformity` handles both the org-wide list AND the
    // just-generated batch view (via ?ids=…), so the batch context is carried
    // back to return the user to the slice they were reviewing.
    const batchIds = params.get("batch")?.split(",").filter(Boolean) ?? []
    const search = batchIds.length > 0 ? `?ids=${batchIds.join(",")}` : ""
    navigate(`${MC_BASE}/customer-studio/conformity${search}`)
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-grey5">
        <Loader2 size={16} className="mr-2 animate-spin" />
        {translate("MC_CS_CONF_EDIT_LOADING")}
      </div>
    )
  }

  if (isError || !detail) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-sm text-grey5">
        <AlertTriangle size={20} className="text-warning" />
        <p className="max-w-sm text-center">
          {translate("MC_CS_CONF_EDIT_LOAD_ERROR")}
        </p>
        <Button variant="outline" onClick={goBackToList}>
          {translate("MC_CS_CONF_EDIT_BACK_TO_INBOX")}
        </Button>
      </div>
    )
  }

  // Keyed by id: the batch pager moves to the next declaration on the same
  // route, and the editor keeps per-declaration state (row expansion, zoom,
  // page) that must not leak from one declaration to the next.
  return (
    <ConformityEditorModuleV2
      key={detail.id}
      detail={detail}
      detailIsFetching={isDetailFetching}
    />
  )
}
